import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import { pointOnFeature } from '@turf/turf'
import { Controller, useFormContext } from 'react-hook-form'
import { Box } from '../../../../components/Box.tsx'
import { I18n } from '../../../../components/I18n.tsx'
import { NumberInput } from '../../../../components/NumberInput.tsx'
import { Stack } from '../../../../components/Stack.tsx'
import type { AssetLocation } from '../../../../data/locations.tsx'
import { ids } from '../../../../ids.ts'
import { useMap } from '../../../../states.ts'
import { type StrictPoint, strictPoint } from '../../../../strict-point.ts'
import { type BaseReportForm, useBaseFormValues } from '../schema.ts'
import { snapPointInsideGeometry } from '../snapPointInsideGeometry.tsx'

export const CustomPointField = ({ selectedLocation, maxAssets }: { selectedLocation: AssetLocation | undefined; maxAssets: number }) => {
	const map = useMap((state) => state.map)
	const { control, setValue } = useFormContext<BaseReportForm>()
	const [customPoint] = useBaseFormValues(['customPoint'])

	const onCustomPointToggled = (enabled: boolean) => {
		if (!enabled) {
			setValue('customPoint', undefined, { shouldValidate: true })
		} else if (selectedLocation) {
			const initialPoint = strictPoint(pointOnFeature(selectedLocation.geometry).geometry)
			setValue('customPoint', initialPoint, { shouldValidate: true })
		}
	}

	return (
		<>
			<FormControlLabel
				disabled={!selectedLocation || selectedLocation.geometry.type === 'Point'}
				checked={!!customPoint}
				control={
					<Switch data-ta={ids.reports.form.areaOfInterest.customPointToggle} onChange={(_, checked) => onCustomPointToggled(checked)} />
				}
				label={
					maxAssets > 1 ? (
						<I18n i18nKey={($) => $.reports.form.customPointSingleAsset} />
					) : (
						<I18n i18nKey={($) => $.reports.form.customPoint} />
					)
				}
			/>
			{customPoint && selectedLocation && (
				<Controller
					control={control}
					name='customPoint'
					render={({ field, fieldState }) => (
						<LatitudeLongitudeFields
							value={customPoint}
							onBlur={field.onBlur}
							error={fieldState.error?.message}
							onChange={(newLatLon) => {
								if (!map) return
								const sanitizedPoint = snapPointInsideGeometry(map, selectedLocation.geometry, newLatLon, {
									maxSnapDistance: { unit: 'kilometers', distance: 0.5 },
								})

								field.onChange(sanitizedPoint)
							}}
						/>
					)}
				/>
			)}
		</>
	)
}

const LatitudeLongitudeFields = ({
	onChange,
	value,
	error,
	onBlur,
}: {
	onChange: (value: StrictPoint) => void
	value: StrictPoint
	error?: string
	onBlur?: () => void
}) => {
	const toFixed = (value: number, decimals: number) => Math.round(value * 10 ** decimals) / 10 ** decimals
	const LATITUDE_LONGITUDE_DECIMALS = 5
	const [longitude, latitude] = value.coordinates

	return (
		<Box>
			<Stack direction='row'>
				<NumberInput
					dataTa={ids.reports.form.areaOfInterest.customPointCoordinate('latitude')}
					value={toFixed(latitude, LATITUDE_LONGITUDE_DECIMALS)}
					onBlur={onBlur}
					label={<I18n i18nKey={($) => $.latitude} />}
					required
					onChange={(e) => e.valid && onChange(strictPoint([longitude, e.validatedValue]))}
					onChangeCommited={(value) => onChange(strictPoint([longitude, value]))}
					step={0.01}
					min={-90}
					max={90}
					decimals={5}
				/>
				<NumberInput
					dataTa={ids.reports.form.areaOfInterest.customPointCoordinate('longitude')}
					onBlur={onBlur}
					label={<I18n i18nKey={($) => $.longitude} />}
					required
					onChange={(e) => e.valid && onChange(strictPoint([e.validatedValue, latitude]))}
					onChangeCommited={(value) => onChange(strictPoint([value, latitude]))}
					value={toFixed(longitude, LATITUDE_LONGITUDE_DECIMALS)}
					step={0.01}
					min={-180}
					max={180}
					decimals={5}
				/>
			</Stack>
			{error ? (
				<Typography pt={1} color='error'>
					{error}
				</Typography>
			) : null}
		</Box>
	)
}
